// The sections of the Settings page (Session SC7B, the calm screens, part 2).
//
// Settings used to be one page of about fifteen cards stacked down an "Office" tab, with a Personal tab and an
// Admin tab beside it, all three panels mounted and hidden by CSS. It is now a short list of sections, ONE on
// screen at a time, each with its own address (/office/settings/email) so a bookmark, a notice link and the
// menu search land on the right one. Every card is the same component it always was, with the same admin-only
// rules: a section only decides WHICH cards are mounted together, never what a card does.
//
// ONE PLACE: this file is the single list of sections (their order, address, plain-English words and search
// words). SettingsPage.tsx maps each id to its cards; nav.ts lights the Settings hub on every section address;
// navSearch.ts searches the hints below, so an old card name ("signature", "postcode cache", "backup") still
// finds Settings, and finds the right section.
//
// The OTHER pages that sit beside Settings in its hub (Office users, Staff activity, Auditor access, Coverage
// zones, Connected AI, Personal data requests, Data retention) are separate routes with their own tab strip
// entries; they are not sections and are not listed here.

export interface SettingsSection {
  /** The URL segment: /office/settings/<id>. */
  id: string;
  /** The label in the section list. */
  label: string;
  /** Plain-English words for the menu search: the names of the cards in this section that EVERY login can see, old and new. */
  hint: string;
  /**
   * The names of the cards in this section that only an ADMIN sees. The menu search adds them for an admin and never for any
   * other login, so an office login is not sent to a section by a word whose card is not on its screen (SC7B review).
   */
  adminHint?: string;
}

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    id: "your-account",
    label: "Your account",
    hint: "Your name, email and role, and the Schedule of Fees download (documents)",
  },
  {
    id: "company",
    label: "Company",
    hint: "Company details, company number, VAT, registered address, bank details, and the LOLER examining body we recommend",
  },
  {
    id: "email",
    label: "Email",
    hint: "Our own email addresses, the company signature, late reply wording, email deliverability (SPF, DKIM, DMARC) and the recent SMS log",
  },
  {
    id: "chasing",
    label: "Chasing and money",
    hint: "Chasing settings, the chase ladders and the supplier chase list, reference rates",
    adminHint: "debt recovery contact, CPI override, payment runway, quote validity, labour cost, call survey delay, inbox chase threshold and knowledge base trusted senders",
  },
  {
    id: "integrations",
    label: "Integrations",
    hint: "Calendar sync",
    adminHint: "Google, Xero, Signable, Stripe, Twilio, the inbound call webhook, map geocoding and the Claude.ai connector instructions",
  },
  {
    id: "automation",
    label: "Automation",
    hint: "Scheduled reports",
    adminHint: "Automatic PPM job creation and automatic draft invoicing",
  },
  {
    id: "data",
    label: "Data and backups",
    hint: "Database backup status",
    adminHint: "database backup download, the postcode cache (postcode lookup cache) and the privacy notice",
  },
  {
    id: "subcontractors",
    label: "Subcontractors",
    hint: "Subcontractor welcome pack documents (CIS notice, agreement wording)",
    adminHint: "the subcontractor Xero payable contact and the standard rate card",
  },
];

export const DEFAULT_SETTINGS_SECTION = SETTINGS_SECTIONS[0]!.id;

/** The address of one section. */
export function settingsSectionPath(id: string): string {
  return id === DEFAULT_SETTINGS_SECTION ? "/office/settings" : `/office/settings/${id}`;
}

/** The whole addresses of every section, the bare one included: how the Settings tab stays lit on each. */
export function settingsSectionPaths(): string[] {
  return SETTINGS_SECTIONS.map((s) => `/office/settings/${s.id}`);
}

/** The section a URL segment names, or null when it names none. Pure. */
export function findSettingsSection(param: string | undefined): SettingsSection | null {
  if (!param) return null;
  return SETTINGS_SECTIONS.find((s) => s.id === param) ?? null;
}
